import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';

export type UnitSystem = 'us-customary' | 'si';
export type QuantityKind =
  | 'diameter'
  | 'flowRate'
  | 'head'
  | 'power'
  | 'pressure'
  | 'velocity';

export type DisplayUnit =
  | 'bpm'
  | 'ft'
  | 'ft/s'
  | 'gpm'
  | 'hp'
  | 'in'
  | 'kPa'
  | 'kW'
  | 'm'
  | 'm/s'
  | 'm3/s'
  | 'mm'
  | 'psi';

export interface DisplayUnitPreferences {
  alternateFlowRate: 'bpm' | 'm3/s';
  diameter: 'in' | 'mm';
  flowRate: 'gpm' | 'm3/s';
  head: 'ft' | 'm';
  power: 'hp' | 'kW';
  pressure: 'psi' | 'kPa';
  velocity: 'ft/s' | 'm/s';
}

interface FormatQuantityOptions {
  maximumFractionDigits?: number;
  unit?: DisplayUnit;
}

interface UnitDisplayContextValue {
  displayUnitForQuantity: (quantity: QuantityKind) => DisplayUnit;
  formatSiValue: (
    value: number,
    quantity: QuantityKind,
    options?: FormatQuantityOptions,
  ) => string;
  setSystem: (system: UnitSystem) => void;
  system: UnitSystem;
  toDisplayValue: (
    value: number,
    quantity: QuantityKind,
    unit?: DisplayUnit,
  ) => number;
  toSiValue: (
    value: number,
    quantity: QuantityKind,
    unit?: DisplayUnit,
  ) => number;
  units: DisplayUnitPreferences;
}

const STORAGE_KEY = 'flow-wizard.display-unit-system.v1';

const UNIT_PRESETS: Record<UnitSystem, DisplayUnitPreferences> = {
  'us-customary': {
    alternateFlowRate: 'bpm',
    diameter: 'in',
    flowRate: 'gpm',
    head: 'ft',
    power: 'hp',
    pressure: 'psi',
    velocity: 'ft/s',
  },
  si: {
    alternateFlowRate: 'm3/s',
    diameter: 'mm',
    flowRate: 'm3/s',
    head: 'm',
    power: 'kW',
    pressure: 'kPa',
    velocity: 'm/s',
  },
};

const DEFAULT_PRECISION: Record<QuantityKind, number> = {
  diameter: 2,
  flowRate: 1,
  head: 1,
  power: 1,
  pressure: 1,
  velocity: 1,
};

const SI_TO_DISPLAY_FACTOR: Record<DisplayUnit, number> = {
  bpm: 60 / 0.158_987_294_928,
  ft: 1 / 0.3048,
  'ft/s': 1 / 0.3048,
  gpm: 60 / 0.003_785_411_784,
  hp: 0.001_341_022_09,
  in: 39.370_078_74,
  kPa: 0.001,
  kW: 0.001,
  m: 1,
  'm/s': 1,
  'm3/s': 1,
  mm: 1_000,
  psi: 0.000_145_037_738,
};

const UNIT_LABEL: Record<DisplayUnit, string> = {
  bpm: 'BPM',
  ft: 'ft',
  'ft/s': 'ft/s',
  gpm: 'GPM',
  hp: 'hp',
  in: 'in',
  kPa: 'kPa',
  kW: 'kW',
  m: 'm',
  'm/s': 'm/s',
  'm3/s': 'm³/s',
  mm: 'mm',
  psi: 'psi',
};

export function displayUnitLabel(unit: DisplayUnit): string {
  return UNIT_LABEL[unit];
}

export function convertSiToDisplay(value: number, unit: DisplayUnit): number {
  return value * SI_TO_DISPLAY_FACTOR[unit];
}

export function convertDisplayToSi(value: number, unit: DisplayUnit): number {
  return value / SI_TO_DISPLAY_FACTOR[unit];
}

const UnitDisplayContext = createContext<UnitDisplayContextValue | null>(null);

function isUnitSystem(value: string | null): value is UnitSystem {
  return value === 'us-customary' || value === 'si';
}

function loadInitialSystem(): UnitSystem {
  try {
    const storedSystem = window.localStorage.getItem(STORAGE_KEY);
    return isUnitSystem(storedSystem) ? storedSystem : 'us-customary';
  } catch {
    return 'us-customary';
  }
}

function defaultUnitForQuantity(
  quantity: QuantityKind,
  units: DisplayUnitPreferences,
): DisplayUnit {
  switch (quantity) {
    case 'diameter':
      return units.diameter;
    case 'flowRate':
      return units.flowRate;
    case 'head':
      return units.head;
    case 'power':
      return units.power;
    case 'pressure':
      return units.pressure;
    case 'velocity':
      return units.velocity;
  }
}

export function UnitDisplayProvider({ children }: PropsWithChildren) {
  const [system, setSystem] = useState<UnitSystem>(loadInitialSystem);
  const units = UNIT_PRESETS[system];

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, system);
    } catch {
      // Storage may be unavailable in hardened browser contexts. In-memory state
      // remains authoritative for the current session.
    }
  }, [system]);

  const formatSiValue = useCallback(
    (
      value: number,
      quantity: QuantityKind,
      options: FormatQuantityOptions = {},
    ) => {
      const unit = options.unit ?? defaultUnitForQuantity(quantity, units);
      const convertedValue = convertSiToDisplay(value, unit);
      const formattedValue = new Intl.NumberFormat(undefined, {
        maximumFractionDigits:
          options.maximumFractionDigits ?? DEFAULT_PRECISION[quantity],
      }).format(convertedValue);

      return `${formattedValue} ${UNIT_LABEL[unit]}`;
    },
    [units],
  );

  const displayUnitForQuantity = useCallback(
    (quantity: QuantityKind) => defaultUnitForQuantity(quantity, units),
    [units],
  );

  const toDisplayValue = useCallback(
    (value: number, quantity: QuantityKind, unit?: DisplayUnit) =>
      convertSiToDisplay(
        value,
        unit ?? defaultUnitForQuantity(quantity, units),
      ),
    [units],
  );

  const toSiValue = useCallback(
    (value: number, quantity: QuantityKind, unit?: DisplayUnit) =>
      convertDisplayToSi(
        value,
        unit ?? defaultUnitForQuantity(quantity, units),
      ),
    [units],
  );

  const value = useMemo<UnitDisplayContextValue>(
    () => ({
      displayUnitForQuantity,
      formatSiValue,
      setSystem,
      system,
      toDisplayValue,
      toSiValue,
      units,
    }),
    [displayUnitForQuantity, formatSiValue, system, toDisplayValue, toSiValue, units],
  );

  return (
    <UnitDisplayContext.Provider value={value}>
      {children}
    </UnitDisplayContext.Provider>
  );
}

export function useUnitDisplay() {
  const context = useContext(UnitDisplayContext);

  if (context === null) {
    throw new Error('useUnitDisplay must be used within UnitDisplayProvider.');
  }

  return context;
}
